import { useEffect, useRef } from 'react';
import type { CanvasState } from './useCanvasTransform';

export function usePan(
  canvasRef: React.RefObject<HTMLCanvasElement>,
  setCanvasState: React.Dispatch<React.SetStateAction<CanvasState>>
) {
  const isSpaceDownRef = useRef(false);
  const isPanningRef = useRef(false);
  const lastPointerRef = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // 입력 필드에 포커스가 있으면 Space 키 처리를 스킵
      const tag = (e.target && (e.target as HTMLElement).tagName) ? 
        (e.target as HTMLElement).tagName.toLowerCase() : '';
      if (tag === 'input' || tag === 'textarea' || 
          (e.target as HTMLElement).isContentEditable) {
        return; // 입력 필드에서는 Space 키를 그대로 통과
      }
      
      if (e.code === 'Space') {
        e.preventDefault();
        if (!isSpaceDownRef.current) {
          isSpaceDownRef.current = true;
          canvas.style.cursor = isPanningRef.current ? 'grabbing' : 'grab';
        }
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      // 입력 필드에 포커스가 있으면 Space 키 처리를 스킵
      const tag = (e.target && (e.target as HTMLElement).tagName) ? 
        (e.target as HTMLElement).tagName.toLowerCase() : '';
      if (tag === 'input' || tag === 'textarea' || 
          (e.target as HTMLElement).isContentEditable) {
        return; // 입력 필드에서는 Space 키를 그대로 통과
      }
      
      if (e.code === 'Space') {
        e.preventDefault();
        isSpaceDownRef.current = false;
        isPanningRef.current = false;
        lastPointerRef.current = null;
        canvas.style.cursor = 'default';
      }
    };

    const handleMouseDown = (e: MouseEvent) => {
      if (!isSpaceDownRef.current && e.button !== 1) return;
      e.preventDefault();
      isPanningRef.current = true;
      lastPointerRef.current = { x: e.clientX, y: e.clientY };
      canvas.style.cursor = 'grabbing';
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isPanningRef.current || !lastPointerRef.current) return;
      e.preventDefault();
      const dx = e.clientX - lastPointerRef.current.x;
      const dy = e.clientY - lastPointerRef.current.y;
      lastPointerRef.current = { x: e.clientX, y: e.clientY };
      setCanvasState(prev => ({
        ...prev,
        offset: { x: prev.offset.x + dx, y: prev.offset.y + dy }
      }));
    };

    const endPan = () => {
      if (!isPanningRef.current) return;
      isPanningRef.current = false;
      lastPointerRef.current = null;
      canvas.style.cursor = isSpaceDownRef.current ? 'grab' : 'default';
    };

    window.addEventListener('keydown', handleKeyDown, { passive: false });
    window.addEventListener('keyup', handleKeyUp, { passive: false });
    canvas.addEventListener('mousedown', handleMouseDown, { passive: false });
    window.addEventListener('mousemove', handleMouseMove, { passive: false });
    window.addEventListener('mouseup', endPan, { passive: false });
    canvas.addEventListener('mouseleave', endPan, { passive: false });

    return () => {
      window.removeEventListener('keydown', handleKeyDown as any);
      window.removeEventListener('keyup', handleKeyUp as any);
      canvas.removeEventListener('mousedown', handleMouseDown as any);
      window.removeEventListener('mousemove', handleMouseMove as any);
      window.removeEventListener('mouseup', endPan as any);
      canvas.removeEventListener('mouseleave', endPan as any);
    };
  }, [canvasRef, setCanvasState]);

  // 외부에서 판별할 수 있게 getter 제공
  const getIsPanning = () => isPanningRef.current;
  const getIsSpaceDown = () => isSpaceDownRef.current;
  return { getIsPanning, getIsSpaceDown };
}


